import React, { useEffect, useRef, useState } from 'react';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import { fromLonLat, transform } from 'ol/proj';
import { CurrentPositionMarker } from './CurrentPositionMarker';
import styles from './OpenLayersMap.module.css';
import './openlayers.css';
import GeoImage from 'ol-ext/source/GeoImage';
import { useSelector } from 'react-redux';
import { selectDigitalMap } from 'ducks/universal/digitalMap';
import ImageLayer from 'ol/layer/Image';
import { OpenLayersPins } from './OpenLayersPins';
import XYZ from 'ol/source/XYZ';
import { getFloorOverlay } from '../util';
import { useMapDisplayContext } from '../MapDisplayContext';

const showDebug = false;

interface OpenLayersMapProps {
  mapId: string;
  onClickPin: (pinKey: string) => void;
  onMapInstanceChange?: (map: Map | null) => void;
}

const DebugOverlay: React.FC<{ mapRef: React.RefObject<Map | null> }> = ({ mapRef }) => {
  const [debugInfo, setDebugInfo] = useState({
    center: '',
    zoom: '',
    rotation: '',
    extent: '',
    resolution: '',
    layers: '',
  });

  useEffect(() => {
    const updateDebugInfo = () => {
      console.log('updateDebugInfo');

      const view = mapRef.current?.getView();

      if (!view) return;

      const center = view?.getCenter();
      const extent = view?.calculateExtent(mapRef.current?.getSize());

      if (center && extent) {
        // Convert center coordinates to lat/lon for readability
        const centerLonLat = transform(center, 'EPSG:3857', 'EPSG:4326');

        // Transform each corner of the extent separately
        const minLonLat = transform([extent[0], extent[1]], 'EPSG:3857', 'EPSG:4326');
        const maxLonLat = transform([extent[2], extent[3]], 'EPSG:3857', 'EPSG:4326');

        setDebugInfo({
          center: `Center: ${centerLonLat[0].toFixed(6)}, ${centerLonLat[1].toFixed(6)}`,
          zoom: `Zoom: ${view?.getZoom()?.toFixed(2)}`,
          rotation: `Rotation: ${((view?.getRotation() * 180) / Math.PI).toFixed(2)}°`,
          extent: `Extent: ${minLonLat[0].toFixed(6)}, ${minLonLat[1].toFixed(
            6
          )}, ${maxLonLat[0].toFixed(6)}, ${maxLonLat[1].toFixed(6)}`,
          resolution: `Resolution: ${view?.getResolution()?.toFixed(2)}`,
          layers: `Layers: ${mapRef.current?.getLayers().getLength()}`,
        });
      }
    };

    const map = mapRef.current;

    // Update on map events
    map?.on('change', updateDebugInfo);
    map?.on('change:resolution' as any, updateDebugInfo);
    map?.on('change:rotation' as any, updateDebugInfo);
    map?.getView()?.on('change', updateDebugInfo);

    // Initial update
    updateDebugInfo();

    return () => {
      map?.un('moveend', updateDebugInfo);
      map?.un('change:resolution' as any, updateDebugInfo);
      map?.un('change:rotation' as any, updateDebugInfo);
    };
  }, [mapRef]);

  return (
    <div className={styles.debugOverlay}>
      <pre>
        {Object.entries(debugInfo).map(([key, value]) => (
          <div key={key}>{value}</div>
        ))}
      </pre>
    </div>
  );
};

const createPngDataUri = (hexColor: string) => {
  // Create a canvas element
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;

  // Get the 2D context
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  // Draw a 1x1 rectangle with the hex color
  ctx.fillStyle = hexColor;
  ctx.fillRect(0, 0, 1, 1);

  // Convert to data URI
  return canvas.toDataURL('image/png');
};

export const OpenLayersMap: React.FC<OpenLayersMapProps> = ({
  mapId,
  onClickPin,
  onMapInstanceChange,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<Map | null>(null);
  const view = useRef<View | null>(null);
  const { selectedFloor } = useMapDisplayContext();

  const digitalMap = useSelector(selectDigitalMap);
  const defaultCenter = digitalMap?.default_map_center;
  const defaultZoom = digitalMap?.default_map_zoom;
  const defaultRotation = digitalMap?.map_rotation;

  const overlay = React.useMemo(() => getFloorOverlay(digitalMap, selectedFloor ?? 1), [
    digitalMap,
    selectedFloor,
  ]);

  // Initialize map
  useEffect(() => {
    const greenUrl = createPngDataUri('#4d766b');

    if (!mapRef.current) return;

    const tileSource = new XYZ({
      url: greenUrl,
    });

    // Create view with rotation support
    view.current = new View({
      center: fromLonLat([defaultCenter?.longitude ?? 0, defaultCenter?.latitude ?? 0]),
      zoom: defaultZoom ?? 0,
      rotation: (-(defaultRotation ?? 0) * Math.PI) / 180,
    });

    if (!overlay) return;

    const scaleFactor =
      overlay.image_projection_width_in_meters /
      overlay.image_aspect_ratio.width /
      Math.cos(((defaultCenter?.latitude ?? 0) * Math.PI) / 180);

    // Create the image layer with proper extent
    const imageLayer = new ImageLayer({
      source: new GeoImage({
        url: overlay.image_url,
        imageCenter: fromLonLat([defaultCenter?.longitude ?? 0, defaultCenter?.latitude ?? 0]),
        imageScale: [scaleFactor, scaleFactor],
        imageRotate: ((defaultRotation ?? 0) * Math.PI) / 180,
      }),
    });

    if (mapInstance.current) {
      // Update existing map layers
      const layers = mapInstance.current.getLayers();
      // Remove old image layer (second layer)
      layers.removeAt(1);
      // Add new image layer
      layers.push(imageLayer);
    } else {
      // Create map instance
      mapInstance.current = new Map({
        target: mapRef.current,
        layers: [new TileLayer({ source: tileSource }), imageLayer],
        view: view.current,
      });
    }

    // Notify parent about the new map instance
    onMapInstanceChange?.(mapInstance.current);

    // Cleanup
    return () => {
      if (mapInstance.current) {
        onMapInstanceChange?.(null); // Notify parent about cleanup
        mapInstance.current.setTarget('');
        mapInstance.current = null;
      }
    };
  }, [mapId, defaultCenter, defaultZoom, defaultRotation, overlay, onMapInstanceChange]);

  return (
    <div ref={mapRef} className={styles.mapWrapper}>
      {mapInstance.current && (
        <>
          <CurrentPositionMarker map={mapInstance.current} />
          <OpenLayersPins map={mapInstance.current} onClickPin={onClickPin} />
          {showDebug && <DebugOverlay mapRef={mapInstance} />}
        </>
      )}
    </div>
  );
};
